import React from 'react';
import { Card, Table } from 'react-bootstrap';
import PropTypes from 'prop-types';

const SkeletonTable = ({ rows = 10, columnNames, columnWidths }) => {
    // Estilos para el skeleton
    const skeletonStyles = {
        skeletonBox: {
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.5s infinite',
            borderRadius: '4px',
            display: 'inline-block'
        },
        skeletonStatus: {
            background: 'linear-gradient(90deg, #e3f2fd 25%, #bbdefb 50%, #e3f2fd 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.5s infinite',
            borderRadius: '12px',
            display: 'inline-block'
        }
    };

    // Tamaños por defecto para las columnas
    const defaultWidths = [
        '30px', '60px', '40px', '50px', '80px', '120px', '90px', '100px', '80px', '90px'
    ];

    // Si hay columnNames, usar su longitud para determinar cuántas columnas y sus nombres
    const colCount = columnNames ? columnNames.length : defaultWidths.length;
    const widths = columnNames
        ? (columnWidths && columnWidths.length === colCount
            ? columnWidths
            : defaultWidths.slice(0, colCount).concat(
                Array(Math.max(0, colCount - defaultWidths.length)).fill('90px')
            ))
        : defaultWidths;

    return (
        <>
            <style>
                {`
          @keyframes skeleton-loading {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
          
          .skeleton-table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .skeleton-table {
            min-width: 100%;
            white-space: nowrap;
          }
          
          /* Responsivo para pantallas pequeñas */
          @media (max-width: 768px) {
            .skeleton-table {
              min-width: 600px;
            }
            
            .skeleton-table th,
            .skeleton-table td {
              padding: 0.375rem 0.25rem !important;
            }
            
            .skeleton-table th {
              font-size: 0.75rem;
            }
          }
          
          /* Responsivo para pantallas extra pequeñas */
          @media (max-width: 576px) {
            .skeleton-table {
              min-width: 500px;
            }
            
            .skeleton-table th,
            .skeleton-table td {
              padding: 0.25rem 0.125rem !important;
            }
            
            .skeleton-table th {
              font-size: 0.7rem;
            }
          }
          
          /* Scrollbar personalizado para mejor UX */
          .skeleton-table-container::-webkit-scrollbar {
            height: 6px;
          }
          
          .skeleton-table-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          .skeleton-table-container::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          .skeleton-table-container::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
            </style>
            <Card className="mb-3">
                <Card.Body className="p-0">
                    <div className="skeleton-table-container">
                        <Table
                            size="sm"
                            striped
                            className="fs--2 mb-0 overflow-hidden skeleton-table"
                        >
                            <thead className="bg-200 text-900 text-nowrap align-middle">
                                <tr>
                                    {columnNames
                                        ? columnNames.map((name, idx) => (
                                            <th className="py-1" key={idx} style={{
                                                width: widths[idx],
                                                maxWidth: widths[idx],
                                                whiteSpace: 'nowrap',
                                                minWidth: widths[idx]
                                            }}>
                                                <span>{name}</span>
                                            </th>
                                        ))
                                        : defaultWidths.map((width, idx) => (
                                            <th className="py-2" key={idx} style={{
                                                minWidth: width
                                            }}>
                                                <div style={{
                                                    ...skeletonStyles.skeletonBox,
                                                    width,
                                                    height: '16px'
                                                }}></div>
                                            </th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: rows }).map((_, rowIdx) => (
                                    <tr key={rowIdx} className="align-middle white-space-nowrap">
                                        {Array.from({ length: colCount }).map((_, colIdx) => (
                                            <td className="py-2" key={colIdx}>
                                                <div
                                                    style={{
                                                        ...skeletonStyles.skeletonBox,
                                                        width:
                                                            colIdx === 1
                                                                ? widths[colIdx] // status
                                                                : colIdx === 5
                                                                    ? (Math.random() * 60 + 80) + 'px'
                                                                    : colIdx === 7
                                                                        ? (Math.random() * 40 + 100) + 'px'
                                                                        : widths[colIdx] || '90px',
                                                        height: colIdx === 1 ? '20px' : '14px',
                                                        minWidth: colIdx === 1 ? widths[colIdx] : '40px',
                                                        ...(colIdx === 1 ? skeletonStyles.skeletonStatus : {})
                                                    }}
                                                ></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

SkeletonTable.propTypes = {
    rows: PropTypes.number,
    columnNames: PropTypes.array,
    columnWidths: PropTypes.array
};

export default SkeletonTable;